"use client";

import { useMemo, useState } from "react";
import { CandidateAvatar } from "@/components/ui/candidate-avatar";
import { Icon } from "@/components/ui/icon";
import { Button } from "@/components/ui/button";
import type { PipelineCandidate } from "@/lib/api/types";
import { MoveToStage } from "./move-to-stage";

/**
 * A re-link reconciliation can leave candidates without a stage — their
 * remote stage was removed while the job's link was dormant, so the local
 * mapping has nothing to resolve to. `held_state === "held_stage_removed"`
 * marks that case; a null `stage_id` is the same condition surfacing on any
 * candidate that otherwise lost its stage. Neither is a normal pipeline
 * state, so they need an explicit stage assignment before they can be
 * advanced/rejected again.
 */
function needsStage(candidate: PipelineCandidate): boolean {
  return candidate.held_state === "held_stage_removed" || candidate.stage_id == null;
}

interface NeedsStageTrayProps {
  jobId: string;
  /** The pipeline candidates already loaded by the page query — filtered
   * client-side, no dedicated endpoint. */
  candidates: PipelineCandidate[];
  /** bulk_move clears held_state server-side, so a plain move resolves the row. */
  onMoveToStage: (candidateId: string, stageId: string) => Promise<void> | void;
}

export function NeedsStageTray({
  jobId,
  candidates,
  onMoveToStage,
}: NeedsStageTrayProps) {
  const held = useMemo(() => candidates.filter(needsStage), [candidates]);
  const [moveTargetId, setMoveTargetId] = useState<string | null>(null);
  const [moving, setMoving] = useState(false);

  if (held.length === 0) return null;

  const handleConfirm = async (stageId: string) => {
    if (!moveTargetId) return;
    setMoving(true);
    try {
      await onMoveToStage(moveTargetId, stageId);
      setMoveTargetId(null);
    } finally {
      setMoving(false);
    }
  };

  return (
    <div
      data-testid="needs-stage-tray"
      className="flex flex-col gap-2 border-b border-border bg-warning/5 px-4 py-3 md:px-8"
    >
      <div className="flex items-center gap-2">
        <Icon name="IconExclamationCircle" size={16} className="text-warning shrink-0" />
        <span className="text-sm font-medium text-heading">
          {held.length} {held.length === 1 ? "candidate needs" : "candidates need"} a
          stage assigned
        </span>
      </div>
      <ul className="flex flex-col gap-1.5">
        {held.map((candidate) => (
          <li
            key={candidate.job_candidate_id}
            className="flex items-center justify-between gap-3 rounded-md bg-surface-elevated px-2.5 py-1.5"
          >
            <div className="flex min-w-0 items-center gap-2.5">
              <CandidateAvatar
                name={candidate.full_name ?? "Unknown"}
                avatarUrl={candidate.avatar_url}
                size={20}
                initialsPx={8}
              />
              <span className="min-w-0 truncate text-sm font-medium text-heading">
                {candidate.full_name ?? "Unknown"}
              </span>
            </div>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => setMoveTargetId(candidate.job_candidate_id)}
            >
              Assign stage
            </Button>
          </li>
        ))}
      </ul>
      <MoveToStage
        open={moveTargetId !== null}
        onOpenChange={(open) => !open && setMoveTargetId(null)}
        jobId={jobId}
        onConfirm={handleConfirm}
        loading={moving}
        count={1}
      />
    </div>
  );
}
